import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import MiniRatings from './MiniRatings';
import { upvoteReview, fetchComments, addComment, addReply, saveKahani, unsaveKahani, reactToComment, reactToReply, editComment, deleteComment } from '../api';

const displayName = (name, hkId) => (/^User \d{4}$/.test(name) ? null : name) || hkId || '?';

export default function ReviewCard({ review, currentUser, delay = 0, onEdit, onDelete, onUnsave, onSeeSimilar, onSignInRequired, autoOpenComments }) {
  const isGuest = currentUser?.isAnonymous || currentUser?.provider === 'anon';
  const isOwnReview = currentUser && (review.authorId === currentUser.id || review.authorId === currentUser.uid);
  const canSave = currentUser && !currentUser.isAnonymous && !isOwnReview;
  const isSaved = canSave && (currentUser.savedKahanis || []).includes(review.id || review._id);
  const [saved, setSaved] = useState(isSaved);
  const [upvoted, setUpvoted] = useState(review.upvotedBy?.includes(currentUser?.id || currentUser?.uid) || false);
  const [votes, setVotes] = useState(review.upvotes || 0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // Comments state
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentCount, setCommentCount] = useState(review.comments || 0);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentError, setCommentError] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // commentId
  const [replyTexts, setReplyTexts] = useState({});
  const [submittingReply, setSubmittingReply] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const commentInputRef = useRef(null);
  const [showGuestPopup, setShowGuestPopup] = useState(false);

  const REACTION_EMOJIS = ['👍', '❤️', '😂', '🔥', '🙏'];
  const [commentReactions, setCommentReactions] = useState({});
  const [openReactionPicker, setOpenReactionPicker] = useState(null);
  const [replyReactions, setReplyReactions] = useState({}); // key: `${commentId}_${replyIndex}`
  const [openReplyReactionPicker, setOpenReplyReactionPicker] = useState(null);

  // Close three-dots menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  // Sync state if review prop changes (e.g. after refresh)
  useEffect(() => {
    const isUpvoted = currentUser ? (review.upvotedBy || []).includes(currentUser.id || currentUser.uid) : false;
    setUpvoted(isUpvoted);
    setVotes(review.upvotes || 0);
    setCommentCount(review.comments || 0);
  }, [review.upvotes, review.comments, review.upvotedBy, currentUser]);

  const reviewId = review.id || review._id;

  const company = review.companyName || review.company;
  const role = review.role;
  const location = review.location;
  const rounds = review.rounds;
  const status = review.status || review.verdict;
  const date = (() => {
    const raw = review.createdAt || review.date;
    if (!raw) return 'Recently';
    const d = new Date(raw);
    if (isNaN(d)) return 'Recently';
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return diffHr === 1 ? '1 hour ago' : `${diffHr} hours ago`;
    if (diffDay === 1) return 'Yesterday';
    if (diffDay < 7) return `${diffDay} days ago`;
    if (diffDay < 14) return 'Last week';
    if (diffDay < 30) return `${Math.floor(diffDay / 7)} weeks ago`;
    if (diffDay < 60) return 'Last month';
    if (diffDay < 365) return `${Math.floor(diffDay / 30)} months ago`;
    return diffDay < 730 ? 'Last year' : `${Math.floor(diffDay / 365)} years ago`;
  })();
  const description = review.description || review.excerpt;
  const salary = review.salary
    ? (typeof review.salary === 'object'
        ? (review.salary.amount ? `₹${(review.salary.amount / 100000).toFixed(1)} LPA` : null)
        : review.salary)
    : null;

  const canComment = currentUser && !currentUser.isAnonymous;
  const canViewComments = currentUser && !currentUser.isAnonymous;

  // Load comments when section is first opened
  useEffect(() => {
    if (showComments && !commentsLoaded) {
      fetchComments(reviewId)
        .then(data => { setComments(data.comments || []); setCommentsLoaded(true); })
        .catch(() => setCommentsLoaded(true));
    }
  }, [showComments, commentsLoaded, reviewId]);

  // Auto-open comments if triggered from live feed navigation
  useEffect(() => {
    if (autoOpenComments) setShowComments(true);
  }, [autoOpenComments]);

  // Focus input when comment section opens
  useEffect(() => {
    if (showComments && canComment && commentInputRef.current) {
      setTimeout(() => commentInputRef.current?.focus(), 100);
    }
  }, [showComments, canComment]);

  // Init reactions once when comments are first loaded — never re-run on comment additions
  useEffect(() => {
    if (commentsLoaded) {
      setCommentReactions(prev => {
        const init = { ...prev };
        comments.forEach(c => {
          if (!init[c.id]) init[c.id] = c.reactions || {};
        });
        return init;
      });
      setReplyReactions(prev => {
        const init = { ...prev };
        comments.forEach(c => {
          (c.replies || []).forEach((r, ri) => {
            const key = `${c.id}_${ri}`;
            if (!init[key]) init[key] = r.reactions || {};
          });
        });
        return init;
      });
    }
  }, [commentsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReact = async (commentId, emoji) => {
    if (!canViewComments) return;
    const myId = currentUser?.id || currentUser?.uid;
    const prev = commentReactions[commentId] || {};
    const reactors = prev[emoji] || [];
    const hasReacted = reactors.includes(myId);
    // Find existing different reaction to swap out (one reaction per user)
    const existingEmoji = REACTION_EMOJIS.find(e => e !== emoji && (prev[e] || []).includes(myId));
    // Optimistic update
    setCommentReactions(r => {
      const updated = { ...r[commentId] };
      if (hasReacted) {
        updated[emoji] = reactors.filter(id => id !== myId);
      } else {
        updated[emoji] = [...reactors, myId];
        if (existingEmoji) updated[existingEmoji] = (updated[existingEmoji] || []).filter(id => id !== myId);
      }
      return { ...r, [commentId]: updated };
    });
    setOpenReactionPicker(null);
    try {
      await reactToComment(reviewId, commentId, emoji);
    } catch (err) {
      // Revert
      setCommentReactions(r => ({ ...r, [commentId]: prev }));
      console.error(err);
    }
  };

  const handleReactToReply = async (commentId, replyId, emoji) => {
    if (!canViewComments) return;
    const myId = currentUser?.id || currentUser?.uid;
    const key = `${commentId}_${replyId}`;
    const prev = replyReactions[key] || {};
    const reactors = prev[emoji] || [];
    const hasReacted = reactors.includes(myId);
    const existingEmoji = REACTION_EMOJIS.find(e => e !== emoji && (prev[e] || []).includes(myId));
    setReplyReactions(r => {
      const updated = { ...(r[key] || {}) };
      if (hasReacted) {
        updated[emoji] = reactors.filter(id => id !== myId);
      } else {
        updated[emoji] = [...reactors, myId];
        if (existingEmoji) updated[existingEmoji] = (updated[existingEmoji] || []).filter(id => id !== myId);
      }
      return { ...r, [key]: updated };
    });
    setOpenReplyReactionPicker(null);
    try {
      await reactToReply(reviewId, commentId, replyId, emoji);
    } catch (err) {
      setReplyReactions(r => ({ ...r, [key]: prev }));
      console.error(err);
    }
  };

  const handleToggleSave = async (e) => {
    e.stopPropagation();
    if (!canSave) return;
    const reviewId = review.id || review._id;
    try {
      if (saved) {
        setSaved(false);
        await unsaveKahani(reviewId);
        if (onUnsave) onUnsave(reviewId);
      } else {
        setSaved(true);
        await saveKahani(reviewId);
      }
    } catch (err) {
      setSaved(s => !s); // revert on error
      console.error(err);
    }
  };

  const handleToggleComments = (e) => {
    e.stopPropagation();
    if (isGuest) { setShowGuestPopup(true); return; }
    setShowComments(v => !v);
  };

  const handleUpvote = async (e) => {
    e.stopPropagation();
    if (isGuest) { setShowGuestPopup(true); return; }
    if (upvoted || isOwnReview) return;
    try {
      setUpvoted(true);
      setVotes(v => v + 1);
      await upvoteReview(reviewId);
    } catch (err) {
      setUpvoted(false);
      setVotes(v => v - 1);
      console.error(err);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!commentText.trim() || submittingComment) return;
    setSubmittingComment(true);
    try {
      const data = await addComment(reviewId, commentText.trim());
      setComments(prev => [...prev, data.comment]);
      setCommentReactions(prev => ({ ...prev, [data.comment.id]: data.comment.reactions || {} }));
      setCommentCount(c => c + 1);
      setCommentText('');
      setCommentError('');
    } catch (err) {
      console.error('Failed to add comment:', err);
      setCommentError('Failed to post comment. Please try again.');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleAddReply = async (e, commentId) => {
    e.preventDefault();
    e.stopPropagation();
    const text = replyTexts[commentId];
    if (!text?.trim() || submittingReply === commentId) return;
    setSubmittingReply(commentId);
    try {
      const data = await addReply(reviewId, commentId, text.trim());
      setComments(prev => prev.map(c => {
        if (c.id !== commentId) return c;
        const newReplies = [...(c.replies || []), data.reply];
        const newIndex = newReplies.length - 1;
        setReplyReactions(r => ({ ...r, [`${commentId}_${newIndex}`]: data.reply.reactions || {} }));
        return { ...c, replies: newReplies };
      }));
      setReplyTexts(prev => ({ ...prev, [commentId]: '' }));
      setReplyingTo(null);
    } catch (err) {
      console.error('Failed to add reply:', err);
    } finally {
      setSubmittingReply(null);
    }
  };

  const handleEditComment = async (e, commentId) => {
    e.preventDefault();
    if (!editCommentText.trim() || savingEdit) return;
    setSavingEdit(true);
    try {
      await editComment(reviewId, commentId, editCommentText.trim());
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, text: editCommentText.trim() } : c));
      setEditingCommentId(null);
      setEditCommentText('');
    } catch (err) {
      console.error('Failed to edit comment:', err);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (deletingCommentId) return;
    setDeletingCommentId(commentId);
    try {
      await deleteComment(reviewId, commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
      setCommentCount(c => Math.max(0, c - 1));
    } catch (err) {
      console.error('Failed to delete comment:', err);
    } finally {
      setDeletingCommentId(null);
    }
  };

  function timeAgo(iso) {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }

  const formatVotes = (n) => n >= 1000 ? (n / 1000).toFixed(1) + 'k' : n;

  const cardStyle = {
    background: review.featured ? 'var(--bg2)' : 'var(--surface)',
    border: review.featured ? '1px solid rgba(2, 132, 199, 0.3)' : '1px solid var(--border)',
    borderRadius: 8, marginBottom: 8, overflow: 'hidden',
    transition: 'border-color 0.2s, transform 0.2s, box-shadow 0.2s',
    animation: `fadeUp 0.35s ease ${delay}s both`,
    cursor: 'pointer', position: 'relative',
    boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.05)',
    fontFamily: 'var(--google-sans)',
  };

  return (
    <div
      style={cardStyle}
      onClick={() => setIsExpanded(!isExpanded)}
      onMouseEnter={e => { e.currentTarget.style.borderColor = review.featured ? 'rgba(2, 132, 199, 0.5)' : 'var(--border2)'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 10px -2px rgba(0, 0, 0, 0.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = review.featured ? 'rgba(2, 132, 199, 0.3)' : 'var(--border)'; e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 4px -1px rgba(0, 0, 0, 0.05)'; }}
    >
      {!review.featured && (
        <div className="review-status-bar" style={{
          height: 2, width: '100%',
          background: status === 'offer' ? 'var(--green)' : status === 'rejected' ? 'var(--red)' : status === 'ghosted' ? 'var(--orange)' : status === 'ongoing' ? '#eab308' : 'var(--blue)',
        }} />
      )}

      <div style={{ padding: '14px 16px' }}>
        {review.featured && (
          <div style={{
            fontFamily: 'var(--google-sans)', fontSize: 10, fontWeight: 700,
            letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--blue)',
            marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--blue)', display: 'inline-block', animation: 'pulse 2s infinite' }} />
            Featured Kahani · {formatVotes(votes)} upvotes
            <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }`}</style>
          </div>
        )}

        {review.featured && (
          <div style={{
            fontFamily: "'Montserrat', sans-serif", fontSize: 16, fontWeight: 700,
            lineHeight: 1.2, color: 'var(--text)', marginBottom: 8,
          }}>
            {company} {role} {location} — Breakdown of All {rounds} Rounds
          </div>
        )}

        {!review.featured && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>
                <span style={{ color: 'var(--blue)' }}>{(/^User \d{4}$/.test(review.authorName) || ['Deleted User','Anonymous','anonymous'].includes(review.authorName) ? null : review.authorName) || review.authorHkId || '?'}</span>
                {' '}posted about{' '}
                <span>{company}</span>
              </div>
              {/* Three-dots menu */}
              {(onEdit || onDelete) && (
                <div ref={menuRef} style={{ position: 'relative', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                  <button
                    onClick={e => { e.stopPropagation(); setShowMenu(v => !v); }}
                    style={{
                      background: showMenu ? 'var(--surface2)' : 'transparent',
                      border: 'none', borderRadius: 6, cursor: 'pointer',
                      padding: '2px 6px', color: 'var(--text3)',
                      fontSize: 16, lineHeight: 1, transition: 'background 0.15s',
                      display: 'flex', alignItems: 'center',
                    }}
                    title="More options"
                  >
                    •••
                  </button>
                  {showMenu && (
                    <div style={{
                      position: 'absolute', top: '110%', right: 0, zIndex: 100,
                      background: 'var(--surface)', border: '1px solid var(--border)',
                      borderRadius: 8, overflow: 'hidden', minWidth: 110,
                      boxShadow: '0 4px 16px rgba(0,0,0,0.13)',
                      animation: 'fadeUp 0.15s ease-out',
                    }}>
                      {onEdit && !isDeleting && (
                        <button
                          onClick={e => { e.stopPropagation(); setShowMenu(false); onEdit(); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 7,
                            width: '100%', padding: '7px 12px', border: 'none',
                            background: 'transparent', cursor: 'pointer',
                            fontSize: 12, fontWeight: 600, color: 'var(--text)',
                            fontFamily: 'var(--sans)', textAlign: 'left',
                            borderBottom: onDelete ? '1px solid var(--border)' : 'none',
                            transition: 'background 0.1s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                          Edit
                        </button>
                      )}
                      {onDelete && !isDeleting && (
                        <button
                          onClick={e => { e.stopPropagation(); setShowMenu(false); setIsDeleting(true); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 7,
                            width: '100%', padding: '7px 12px', border: 'none',
                            background: 'transparent', cursor: 'pointer',
                            fontSize: 12, fontWeight: 600, color: '#ef4444',
                            fontFamily: 'var(--sans)', textAlign: 'left',
                            transition: 'background 0.1s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.06)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          </svg>
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                  {/* Confirm delete bar */}
                  {isDeleting && (
                    <div style={{
                      position: 'absolute', top: '110%', right: 0, zIndex: 100,
                      background: 'var(--surface)', border: '1px solid var(--border)',
                      borderRadius: 10, padding: '12px 14px', minWidth: 200,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                      animation: 'fadeUp 0.15s ease-out',
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', marginBottom: 10, fontFamily: 'var(--sans)' }}>
                        Delete this kahani?
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={e => { e.stopPropagation(); setIsDeleting(false); }}
                          style={{
                            flex: 1, padding: '7px', borderRadius: 7, border: '1px solid var(--border)',
                            background: 'transparent', fontSize: 12, fontWeight: 700,
                            color: 'var(--text2)', cursor: 'pointer', fontFamily: 'var(--sans)',
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); setIsDeleting(false); setShowMenu(false); onDelete(); }}
                          style={{
                            flex: 1, padding: '7px', borderRadius: 7, border: 'none',
                            background: '#ef4444', fontSize: 12, fontWeight: 700,
                            color: '#fff', cursor: 'pointer', fontFamily: 'var(--sans)',
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 6, fontWeight: 500 }}>
              {role} · {location} · {date}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              <Pill color="var(--orange)" bg="rgba(249, 115, 22, 0.1)">⚙ {rounds} R</Pill>
              {status === 'offer' && <Pill color="var(--green)" bg="rgba(16, 185, 129, 0.1)">✅ Offer Received</Pill>}
              {status === 'rejected' && <Pill color="var(--red)" bg="rgba(239, 68, 68, 0.1)">❌ Rejected</Pill>}
              {status === 'ghosted' && <Pill color="var(--red)" bg="rgba(239, 68, 68, 0.1)">👻 Ghosted</Pill>}
              {status === 'ongoing' && <Pill color="var(--blue)" bg="rgba(14, 165, 233, 0.1)">⏳ In Progress</Pill>}
              {review.timeline && <Pill color="var(--blue)" bg="rgba(2, 132, 199, 0.1)">⏱ {review.timeline}</Pill>}
              {salary && <Pill color="var(--text2)" bg="var(--surface2)">💰 {salary}</Pill>}
            </div>
          </div>
        )}

        {review.featured && (
          <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
            <Pill color="var(--orange)" bg="rgba(249, 115, 22, 0.1)">⚙ {rounds} Rounds</Pill>
            <Pill color="var(--blue)" bg="rgba(2, 132, 199, 0.1)">📍 {location}</Pill>
            <Pill color="var(--green)" bg="rgba(16, 185, 129, 0.1)">✅ Offer Received</Pill>
            {review.timeline && <Pill color="var(--blue)" bg="rgba(2, 132, 199, 0.1)">⏱ {review.timeline}</Pill>}
            {salary && <Pill color="var(--text2)" bg="var(--surface2)">💰 {salary}</Pill>}
          </div>
        )}

        {description && (
          <div style={{ margin: '10px 0' }}>
            <div style={{
              fontSize: 17, lineHeight: 1.8, color: 'var(--text)',
              fontWeight: 400, fontFamily: 'var(--google-sans)',
              display: isExpanded ? 'block' : '-webkit-box',
              WebkitLineClamp: isExpanded ? 'unset' : 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {description}
            </div>
            {!isExpanded && (
              <span style={{ fontSize: 11, color: 'var(--blue)', fontWeight: 600, marginTop: 4, display: 'inline-block' }}>
                see more..
              </span>
            )}
          </div>
        )}

        {isExpanded && review.ratings && Object.keys(review.ratings).length > 0 && (
          <div style={{ marginTop: 8 }} onClick={e => e.stopPropagation()}>
            <MiniRatings ratings={review.ratings} />
          </div>
        )}

        {isExpanded && review.interviewQuestion && (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: 'var(--surface2)', border: '1px dashed var(--border)' }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 4 }}>Interview Question</div>
            <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.5 }}>{review.interviewQuestion}</div>
          </div>
        )}

        {/* Footer actions */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 4,
          paddingTop: 8, borderTop: '1px solid var(--border)',
          marginTop: 8
        }}>
          <FootAction onClick={handleUpvote} active={upvoted} disabled={isOwnReview}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4L3 15h6v5h6v-5h6z" /></svg>
            {formatVotes(votes)}
          </FootAction>
          <FootAction onClick={handleToggleComments} active={showComments}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
            {commentCount}
          </FootAction>
          {canSave && (
            <FootAction onClick={handleToggleSave} active={saved} title={saved ? 'Remove from saved' : 'Save kahani'}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill={saved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
              </svg>
              {saved ? 'Saved' : 'Save'}
            </FootAction>
          )}
          {onSeeSimilar && (
            <FootAction onClick={(e) => { e.stopPropagation(); onSeeSimilar(review); }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              See Similar
            </FootAction>
          )}
          {(!onEdit && !onDelete) && review.validatedBy?.length > 0 && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text3)', fontWeight: 500 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }} />
              Verified ({review.validatedBy.length})
            </div>
          )}
        </div>

        {/* ── Guest Sign-In Popup ── */}
        {showGuestPopup && ReactDOM.createPortal(
          <div
            onClick={(e) => { e.stopPropagation(); setShowGuestPopup(false); }}
            style={{
              position: 'fixed', inset: 0, zIndex: 2000,
              background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: '#fff', borderRadius: 20, padding: '32px 28px',
                maxWidth: 360, width: '100%', textAlign: 'center',
                boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
                animation: 'fadeUp 0.25s ease-out',
              }}
            >
              <div style={{ fontSize: 40, marginBottom: 12 }}>👋</div>
              <h2 style={{ fontFamily: 'var(--display)', fontSize: 18, fontWeight: 900, margin: '0 0 8px', color: 'var(--text)' }}>
                Sign in to interact
              </h2>
              <p style={{ fontSize: 13, color: 'var(--text3)', margin: '0 0 24px', lineHeight: 1.6 }}>
                Create a free account or sign in to like and comment on kahanis.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button
                  onClick={() => { setShowGuestPopup(false); if (onSignInRequired) onSignInRequired(); }}
                  style={{
                    background: 'var(--blue)', color: '#fff', border: 'none',
                    padding: '13px', borderRadius: 10, fontSize: 14, fontWeight: 700,
                    cursor: 'pointer', fontFamily: 'var(--sans)',
                    boxShadow: '0 4px 12px rgba(14,165,233,0.3)',
                  }}
                >
                  Sign In / Sign Up
                </button>
                <button
                  onClick={() => setShowGuestPopup(false)}
                  style={{
                    background: 'transparent', color: 'var(--text3)',
                    border: '1px solid var(--border)',
                    padding: '12px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'var(--sans)',
                  }}
                >
                  Not Now
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* ── Comments Section ── */}
        {showComments && (
          <div
            className="comments-section"
            onClick={e => e.stopPropagation()}
            style={{
            marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 10,
          }}>
            {commentError && (
              <div style={{ fontSize: 11, color: '#ef4444', marginBottom: 8, fontWeight: 600 }}>{commentError}</div>
            )}
            {/* Add comment form */}
            {canComment ? (
              <form onSubmit={handleAddComment} style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'flex-start' }}>
                <div style={{
                  width: 26, height: 26, borderRadius: '50%', background: 'var(--blue)',
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, flexShrink: 0,
                }}>
                  {(currentUser?.displayName || '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, display: 'flex', gap: 6 }}>
                  <input
                    ref={commentInputRef}
                    value={commentText}
                    onChange={e => setCommentText(e.target.value)}
                    placeholder="Add a comment…"
                    style={{
                      flex: 1, padding: '6px 10px', borderRadius: 20,
                      border: '1px solid var(--border)', background: 'var(--surface2)',
                      fontSize: 12, color: 'var(--text)', outline: 'none',
                      fontFamily: 'var(--sans)',
                    }}
                  />
                  <button
                    type="submit"
                    disabled={!commentText.trim() || submittingComment}
                    style={{
                      padding: '6px 12px', borderRadius: 20, border: 'none',
                      background: commentText.trim() ? 'var(--blue)' : 'var(--surface2)',
                      color: commentText.trim() ? '#fff' : 'var(--text3)',
                      fontSize: 11, fontWeight: 700, cursor: commentText.trim() ? 'pointer' : 'default',
                      fontFamily: 'var(--sans)', transition: 'all 0.15s', flexShrink: 0,
                    }}
                  >
                    {submittingComment ? '…' : 'Post'}
                  </button>
                </div>
              </form>
            ) : (
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10, fontStyle: 'italic' }}>
                Sign in to leave a comment
              </div>
            )}

            {/* Comments list */}
            {!commentsLoaded ? (
              <div style={{ fontSize: 12, color: 'var(--text3)', padding: '8px 0' }}>Loading comments…</div>
            ) : comments.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text3)', padding: '4px 0' }}>No comments yet. Be the first!</div>
            ) : (
              comments.map(comment => {
                const myId = currentUser?.id || currentUser?.uid;
                const hasMyReaction = REACTION_EMOJIS.some(e => (commentReactions[comment.id]?.[e] || []).includes(myId));
                return (
                <div key={comment.id} className="comment-item" style={{ marginBottom: 10 }}>
                  {/* Comment */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <div style={{
                      width: 26, height: 26, borderRadius: '50%', background: 'var(--surface2)',
                      border: '1px solid var(--border)', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0,
                      color: 'var(--text2)',
                    }}>
                      {displayName(comment.authorName, comment.authorHkId).charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Comment bubble */}
                      <div style={{
                        background: 'var(--surface2)', borderRadius: '0 12px 12px 12px',
                        padding: '7px 10px 7px 10px', position: 'relative',
                      }}>
                        {/* Top-right: reaction pills + add-reaction button */}
                        <div style={{
                          position: 'absolute', top: -10, right: 6,
                          display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'nowrap',
                        }}>
                          {/* Existing reaction pills */}
                          {REACTION_EMOJIS.map(emoji => {
                            const reactors = (commentReactions[comment.id] || {})[emoji] || [];
                            if (reactors.length === 0) return null;
                            const isMine = reactors.includes(myId);
                            return (
                              <button
                                key={emoji}
                                onClick={e => { e.stopPropagation(); if (canViewComments) handleReact(comment.id, emoji); }}
                                title={isMine ? 'Click to remove your reaction' : undefined}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 2,
                                  padding: '2px 6px', borderRadius: 20, fontSize: 11,
                                  border: `1.5px solid ${isMine ? 'var(--blue)' : 'var(--border2)'}`,
                                  background: isMine ? 'rgba(14,165,233,0.12)' : 'var(--surface)',
                                  cursor: canViewComments ? 'pointer' : 'default',
                                  color: isMine ? 'var(--blue)' : 'var(--text2)',
                                  fontWeight: 700, fontFamily: 'var(--mono)',
                                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                  transition: 'all 0.15s',
                                }}
                              >
                                <span style={{ fontSize: 13 }}>{emoji}</span>
                                <span style={{ fontSize: 10 }}>{reactors.length}</span>
                              </button>
                            );
                          })}

                          {/* 😊+ add button — only shown when user has NOT reacted */}
                          {canViewComments && !hasMyReaction && (
                            <div style={{ position: 'relative' }}>
                              <button
                                onClick={e => { e.stopPropagation(); setOpenReactionPicker(openReactionPicker === comment.id ? null : comment.id); }}
                                style={{
                                  background: 'var(--surface)', border: '1.5px dashed var(--border2)',
                                  borderRadius: 20, padding: '2px 6px', cursor: 'pointer',
                                  fontSize: 12, color: 'var(--text3)', lineHeight: 1.4,
                                  fontFamily: 'var(--sans)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                                  transition: 'border-color 0.15s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--blue)'}
                                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border2)'}
                              >
                                😊+
                              </button>
                              {openReactionPicker === comment.id && (
                                <div
                                  onClick={e => e.stopPropagation()}
                                  style={{
                                    position: 'absolute', top: '110%', right: 0, marginTop: 4,
                                    background: 'var(--surface)', border: '1px solid var(--border)',
                                    borderRadius: 12, padding: '6px 8px', display: 'flex', gap: 2,
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.14)', zIndex: 300,
                                  }}
                                >
                                  {REACTION_EMOJIS.map(e => (
                                    <button
                                      key={e}
                                      onClick={() => { handleReact(comment.id, e); setOpenReactionPicker(null); }}
                                      style={{
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        fontSize: 18, padding: '3px 5px', borderRadius: 8,
                                        transition: 'background 0.1s',
                                      }}
                                      onMouseEnter={ev => ev.currentTarget.style.background = 'var(--surface2)'}
                                      onMouseLeave={ev => ev.currentTarget.style.background = 'none'}
                                    >
                                      {e}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>
                          {displayName(comment.authorName, comment.authorHkId)}
                        </div>
                        {editingCommentId === comment.id ? (
                          <form onSubmit={e => handleEditComment(e, comment.id)} style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                            <input
                              autoFocus
                              value={editCommentText}
                              onChange={e => setEditCommentText(e.target.value)}
                              style={{
                                flex: 1, padding: '4px 8px', borderRadius: 8,
                                border: '1px solid var(--border)', background: 'var(--surface)',
                                fontSize: 12, color: 'var(--text)', outline: 'none', fontFamily: 'var(--sans)',
                              }}
                            />
                            <button type="submit" disabled={savingEdit || !editCommentText.trim()} style={{
                              padding: '4px 10px', borderRadius: 8, border: 'none',
                              background: 'var(--blue)', color: '#fff', fontSize: 10, fontWeight: 700,
                              cursor: 'pointer', fontFamily: 'var(--sans)',
                            }}>{savingEdit ? '…' : 'Save'}</button>
                            <button type="button" onClick={() => setEditingCommentId(null)} style={{
                              padding: '4px 8px', borderRadius: 8, border: '1px solid var(--border)',
                              background: 'none', color: 'var(--text3)', fontSize: 10, fontWeight: 700,
                              cursor: 'pointer', fontFamily: 'var(--sans)',
                            }}>Cancel</button>
                          </form>
                        ) : (
                          <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.4 }}>{comment.text}</div>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: 10, marginTop: 3, paddingLeft: 4, alignItems: 'center' }}>
                        <span style={{ fontSize: 10, color: 'var(--text3)' }}>{timeAgo(comment.createdAt)}</span>
                        {canComment && (
                          <button
                            onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                            style={{
                              background: 'none', border: 'none', fontSize: 10, fontWeight: 700,
                              color: replyingTo === comment.id ? 'var(--blue)' : 'var(--text3)',
                              cursor: 'pointer', padding: 0, fontFamily: 'var(--sans)',
                            }}
                          >
                            Reply
                          </button>
                        )}
                        {comment.authorId === myId && (
                          <>
                            <button
                              onClick={() => { setEditingCommentId(comment.id); setEditCommentText(comment.text); setReplyingTo(null); }}
                              style={{ background: 'none', border: 'none', fontSize: 10, fontWeight: 700, color: 'var(--text3)', cursor: 'pointer', padding: 0, fontFamily: 'var(--sans)' }}
                            >Edit</button>
                            <button
                              onClick={() => handleDeleteComment(comment.id)}
                              disabled={deletingCommentId === comment.id}
                              style={{ background: 'none', border: 'none', fontSize: 10, fontWeight: 700, color: '#ef4444', cursor: 'pointer', padding: 0, fontFamily: 'var(--sans)' }}
                            >{deletingCommentId === comment.id ? '…' : 'Delete'}</button>
                          </>
                        )}
                      </div>

                      {/* Replies */}
                      {(comment.replies || []).map((reply, ri) => {
                        const replyKey = `${comment.id}_${reply.id || ri}`;
                        const replyRxns = replyReactions[replyKey] || {};
                        const hasMyReplyReaction = REACTION_EMOJIS.some(e => (replyRxns[e] || []).includes(myId));
                        return (
                        <div key={reply.id || ri} style={{ display: 'flex', gap: 6, marginTop: 6, paddingLeft: 8 }}>
                          <div style={{
                            width: 22, height: 22, borderRadius: '50%', background: 'var(--surface2)',
                            border: '1px solid var(--border)', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0,
                            color: 'var(--text2)',
                          }}>
                            {displayName(reply.authorName, reply.authorHkId).charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              background: 'var(--surface2)', borderRadius: '0 10px 10px 10px',
                              padding: '5px 9px', position: 'relative',
                            }}>
                              {/* Reply reaction pills + picker */}
                              <div style={{
                                position: 'absolute', top: -10, right: 6,
                                display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'nowrap',
                              }}>
                                {REACTION_EMOJIS.map(emoji => {
                                  const reactors = replyRxns[emoji] || [];
                                  if (reactors.length === 0) return null;
                                  const isMine = reactors.includes(myId);
                                  return (
                                    <button
                                      key={emoji}
                                      onClick={e => { e.stopPropagation(); if (canViewComments) handleReactToReply(comment.id, reply.id || ri, emoji); }}
                                      title={isMine ? 'Click to remove your reaction' : undefined}
                                      style={{
                                        display: 'flex', alignItems: 'center', gap: 2,
                                        padding: '2px 6px', borderRadius: 20, fontSize: 11,
                                        border: `1.5px solid ${isMine ? 'var(--blue)' : 'var(--border2)'}`,
                                        background: isMine ? 'rgba(14,165,233,0.12)' : 'var(--surface)',
                                        cursor: canViewComments ? 'pointer' : 'default',
                                        color: isMine ? 'var(--blue)' : 'var(--text2)',
                                        fontWeight: 700, fontFamily: 'var(--mono)',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                        transition: 'all 0.15s',
                                      }}
                                    >
                                      <span style={{ fontSize: 12 }}>{emoji}</span>
                                      <span style={{ fontSize: 10 }}>{reactors.length}</span>
                                    </button>
                                  );
                                })}
                                {canViewComments && !hasMyReplyReaction && (
                                  <div style={{ position: 'relative' }}>
                                    <button
                                      onClick={e => { e.stopPropagation(); setOpenReplyReactionPicker(openReplyReactionPicker === replyKey ? null : replyKey); }}
                                      style={{
                                        background: 'var(--surface)', border: '1.5px dashed var(--border2)',
                                        borderRadius: 20, padding: '2px 5px', cursor: 'pointer',
                                        fontSize: 11, color: 'var(--text3)', lineHeight: 1.4,
                                        fontFamily: 'var(--sans)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                                        transition: 'border-color 0.15s',
                                      }}
                                      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--blue)'}
                                      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border2)'}
                                    >
                                      😊+
                                    </button>
                                    {openReplyReactionPicker === replyKey && (
                                      <div
                                        onClick={e => e.stopPropagation()}
                                        style={{
                                          position: 'absolute', top: '110%', right: 0, marginTop: 4,
                                          background: 'var(--surface)', border: '1px solid var(--border)',
                                          borderRadius: 12, padding: '6px 8px', display: 'flex', gap: 2,
                                          boxShadow: '0 4px 16px rgba(0,0,0,0.14)', zIndex: 300,
                                        }}
                                      >
                                        {REACTION_EMOJIS.map(e => (
                                          <button
                                            key={e}
                                            onClick={() => { handleReactToReply(comment.id, reply.id || ri, e); setOpenReplyReactionPicker(null); }}
                                            style={{
                                              background: 'none', border: 'none', cursor: 'pointer',
                                              fontSize: 18, padding: '3px 5px', borderRadius: 8,
                                              transition: 'background 0.1s',
                                            }}
                                            onMouseEnter={ev => ev.currentTarget.style.background = 'var(--surface2)'}
                                            onMouseLeave={ev => ev.currentTarget.style.background = 'none'}
                                          >
                                            {e}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text)', marginBottom: 1 }}>
                                {displayName(reply.authorName, reply.authorHkId)}
                              </div>
                              <div style={{ fontSize: 11.5, color: 'var(--text2)', lineHeight: 1.4 }}>{reply.text}</div>
                            </div>
                            <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2, paddingLeft: 4 }}>
                              {timeAgo(reply.createdAt)}
                            </div>
                          </div>
                        </div>
                        );
                      })}

                      {/* Reply form */}
                      {replyingTo === comment.id && (
                        <form
                          onSubmit={e => handleAddReply(e, comment.id)}
                          style={{ display: 'flex', gap: 6, marginTop: 8, paddingLeft: 8 }}
                        >
                          <input
                            autoFocus
                            value={replyTexts[comment.id] || ''}
                            onChange={e => setReplyTexts(prev => ({ ...prev, [comment.id]: e.target.value }))}
                            placeholder={`Reply to ${displayName(comment.authorName, comment.authorHkId)}…`}
                            style={{
                              flex: 1, padding: '5px 10px', borderRadius: 20,
                              border: '1px solid var(--border)', background: 'var(--surface2)',
                              fontSize: 11.5, color: 'var(--text)', outline: 'none',
                              fontFamily: 'var(--sans)',
                            }}
                          />
                          <button
                            type="submit"
                            disabled={!replyTexts[comment.id]?.trim() || submittingReply === comment.id}
                            style={{
                              padding: '5px 10px', borderRadius: 20, border: 'none',
                              background: replyTexts[comment.id]?.trim() ? 'var(--blue)' : 'var(--surface2)',
                              color: replyTexts[comment.id]?.trim() ? '#fff' : 'var(--text3)',
                              fontSize: 10, fontWeight: 700,
                              cursor: replyTexts[comment.id]?.trim() ? 'pointer' : 'default',
                              fontFamily: 'var(--sans)', flexShrink: 0,
                            }}
                          >
                            {submittingReply === comment.id ? '…' : 'Reply'}
                          </button>
                        </form>
                      )}
                    </div>
                  </div>
                </div>
              ); })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Pill({ color, bg, children }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
      color, background: bg, border: `1px solid ${bg === 'transparent' ? 'var(--border)' : bg}`
    }}>{children}</span>
  );
}

function FootAction({ children, onClick, active, disabled }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => !disabled && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
        color: active ? 'var(--blue)' : hovered ? 'var(--text)' : 'var(--text2)',
        background: hovered && !disabled ? 'var(--surface2)' : 'transparent',
        border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'var(--sans)',
        opacity: disabled ? 0.5 : 1, transition: 'all 0.2s'
      }}
    >
      {children}
    </button>
  );
}
